import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import { Form, Table } from 'react-bootstrap';
import { IconButton, Tooltip } from '@sumologic/yama-ds-components';
import { generateFieldName } from '~/modules/getArtifactsValidationSchema';

import Translate from '~/components/Translate/Translate';
import Select from '~/components/Select/Select';
import Icon from '~/components/Icon/Icon';

function IncidentArtifactTable({
    artifactItems,
    artifactContainerId,
    cefFields,
    formProps,
    updateArtifactItem,
    addArtifactItem,
    removeArtifactItem,
    editCef
}) {
    useEffect(() => {
        // mm: if container is empty add a default item
        if (addArtifactItem && !artifactItems.length) {
            _addEmptyArtifactItem();
        }
    }, [artifactContainerId]);

    /**
     *
     * @param {function} [mutator] - Specifying mutator callback you can manipulate the cefField object before is
     * returned in result list
     * @returns {Array}
     */
    const getAvailableCefFields = mutator => {
        mutator =
            mutator ||
            function (a) {
                return a;
            };
        //sst: create array with keyName already used
        let keyToIgnore = [];
        keyToIgnore = artifactItems.reduce((acc, item) => {
            if (item.keyName) acc.push(item.keyName);
            return acc;
        }, []);

        return Object.keys(cefFields).reduce((acc, key) => {
            if (!keyToIgnore.includes(cefFields[key].keyName)) {
                const cefField = cefFields[key];
                acc.push(mutator(cefField));
            }
            return acc;
        }, []);
    };

    /**
     *
     * @param targetFieldId
     * @returns {string|*}
     * @private
     */
    const _getDataForTarget = targetFieldId => {
        for (const key in cefFields) {
            if (!cefFields.hasOwnProperty(key)) continue;
            if (cefFields[key].keyName === targetFieldId) return cefFields[key];
        }
        return '';
    };

    const _addEmptyArtifactItem = () => {
        const defaultItem = _createDefaultItem();
        addArtifactItem(defaultItem);
    };

    const _createDefaultItem = () => {
        const cefFields = getAvailableCefFields();
        return {
            ...cefFields[0],
            value: '',
            id: artifactItems.length
        };
    };

    const _updateArtifactValue = (index, value) => {
        updateArtifactItem?.(index, { value: value });
    };

    const _updateArtifactTargetField = (index, event, callback) => {
        const dataTarget = _getDataForTarget(event.value);
        updateArtifactItem(index, dataTarget, callback);
    };

    const _renderOptionsCefField = () => {
        return getAvailableCefFields(cefField => {
            return {
                value: cefField.keyName,
                label: cefField.fullName,
                title: cefField.description
            };
        });
    };

    const _renderTableRow = (element, index) => {
        const item = Object.assign({}, element);
        const options = _renderOptionsCefField();
        let handleId = generateFieldName(artifactContainerId, index);
        let selectedValue = item.keyName
            ? {
                  value: item.keyName,
                  label: item.fullName,
                  title: item.description
              }
            : options[0];

        return (
            <tr key={item.keyName}>
                <td>
                    {(updateArtifactItem || formProps) &&
                    (editCef || item.editCef) ? (
                        <Form.Group
                            controlId={`${handleId}__targetFieldId`}
                            className={'form-group'}
                        >
                            <Select
                                placeholder={
                                    <Translate message="Select target field" />
                                }
                                onChange={event => {
                                    const validateForm = () =>
                                        formProps.validateField(handleId);
                                    if (formProps)
                                        formProps.handleChange({
                                            target: {
                                                name: handleId,
                                                value: item.value
                                            }
                                        });
                                    if (updateArtifactItem)
                                        _updateArtifactTargetField(
                                            index,
                                            event,
                                            validateForm
                                        );
                                }}
                                value={selectedValue}
                                options={options}
                            />
                        </Form.Group>
                    ) : (
                        item.fullName
                    )}
                </td>
                <td>
                    {updateArtifactItem || formProps ? (
                        <Form.Group
                            controlId={handleId}
                            className={'form-group'}
                        >
                            <Translate message="Value">
                                {text => {
                                    const initialValue = item.value;
                                    let lastValue = initialValue;
                                    return (
                                        <Form.Control
                                            placeholder={text}
                                            value={item.value}
                                            // defaultValue={initialValue}
                                            autoComplete="off"
                                            name={handleId}
                                            onChange={event => {
                                                formProps?.handleChange(event);
                                                _updateArtifactValue(
                                                    index,
                                                    event.target.value
                                                );
                                            }}
                                            onBlur={event => {
                                                const currentValue =
                                                    event.target.value;
                                                if (
                                                    currentValue !== lastValue
                                                ) {
                                                    lastValue = currentValue;
                                                }
                                            }}
                                        />
                                    );
                                }}
                            </Translate>
                            {formProps?.errors[handleId] && (
                                <Form.Control.Feedback
                                    type="invalid"
                                    className="d-block text-truncate"
                                >
                                    {formProps.errors[handleId]}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    ) : (
                        item.value
                    )}
                </td>
                <td>
                    {removeArtifactItem && (
                        <Tooltip
                            title={<Translate message="Remove artifact" />}
                            placement="bottom"
                            arrow
                        >
                            <IconButton
                                size="small"
                                className="remove-artifact"
                                onClick={() => removeArtifactItem(index)}
                                data-testid={'remove-artifact'}
                            >
                                <Icon
                                    className="cross"
                                    type="cross2"
                                    size="sm"
                                />
                            </IconButton>
                        </Tooltip>
                    )}
                </td>
            </tr>
        );
    };

    //console.count('IncidentArtifactTable - render');
    return (
        <Table className="incident-artifact-table rounded">
            <thead>
                <tr>
                    <th>
                        <Translate message="Target field id" />
                    </th>
                    <th>
                        <Translate message="Value" />
                    </th>
                    <th className="commands">
                        {addArtifactItem && (
                            <Tooltip
                                title={<Translate message="Add artifact" />}
                                placement="bottom"
                                arrow
                            >
                                <IconButton
                                    size="small"
                                    onClick={_addEmptyArtifactItem}
                                >
                                    <Icon type="plus" size="sm" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </th>
                </tr>
            </thead>
            <tbody>{artifactItems?.map(_renderTableRow)}</tbody>
        </Table>
    );
}

IncidentArtifactTable.propTypes = {
    artifactItems: PropTypes.array,
    artifactContainerId: PropTypes.number,
    cefFields: PropTypes.object,
    formProps: PropTypes.object,
    updateArtifactItem: PropTypes.func,
    addArtifactItem: PropTypes.func,
    removeArtifactItem: PropTypes.func,
    editCef: PropTypes.bool
};

IncidentArtifactTable.defaultProps = {
    artifactItems: [],
    artifactContainerId: 0,
    cefFields: {},
    editCef: true
};

export default IncidentArtifactTable;
